import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { CustomModal, MultiSelectFilterPicker, Page, PageContentHeader, PageEmptyState, PageErrorState, Searchbox, TableList, Typography, useDidUpdate, useOverlayContext } from 'seagull';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import LoadingIndicator from '~/components/LoadingIndicator';
import { ErrorText } from '~/constants';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { modalSize } from '~/infra/modals';
import { handlePromise } from '~/infra/utils';
import NoDataIcon from '~/integrations/assets/NoDataIcon';
import { serviceTemplateModel } from '~/models';
import { PracticeManagement } from '~/types/api.model';
import { getPMDetails } from '../pm.utils';
import PMMapServiceField from '../PMMapServiceField/PMMapServiceField';
import styles from './PMMapServicesModal.module.scss';
function getTableConfig(pmName) {
    const tableConfig = [
        {
            columnDef: {
                width: [
                    100,
                    500
                ]
            },
            headerItem: {
                value: 'Anchor service'
            }
        },
        {
            columnDef: {
                width: 550
            },
            headerItem: {
                value: "".concat(getPMDetails(pmName).name, " ").concat(getPMDetails(pmName).template)
            }
        }
    ];
    return {
        columnDefs: tableConfig.map((config)=>config.columnDef),
        headerItems: tableConfig.map((config)=>config.headerItem)
    };
}
var MappedStatus = /*#__PURE__*/ function(MappedStatus) {
    MappedStatus["Mapped"] = "mapped";
    MappedStatus["Unmapped"] = "unmapped";
    return MappedStatus;
}(MappedStatus || {});
function filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds) {
    const trimmedLowercaseSearch = search.trim().toLowerCase();
    return templates.filter((template)=>{
        const includedBySearch = !trimmedLowercaseSearch || template.name.toLowerCase().includes(trimmedLowercaseSearch);
        const isMapped = getServiceTemplateIntegratedIds(template).length > 0;
        const includedByMappedStatus = mappedStatusFilter.length === 0 || mappedStatusFilter.includes("mapped") && isMapped || mappedStatusFilter.includes("unmapped") && !isMapped;
        return includedBySearch && includedByMappedStatus;
    });
}
const mappedStatusFilterOptions = [
    {
        value: "mapped",
        label: 'Mapped'
    },
    {
        value: "unmapped",
        label: 'Unmapped'
    }
];
function PMMapServiceRow(param) {
    let { template, testId, items, getServiceTemplateIntegratedIds, onMapService } = param;
    const [integratedIds, setIntegratedIds] = useState(()=>getServiceTemplateIntegratedIds(template));
    useDidUpdate(()=>setIntegratedIds(getServiceTemplateIntegratedIds(template)), [
        template
    ]);
    const isDeleted = serviceTemplateModel.isDeleted(template);
    return /*#__PURE__*/ _jsx(TableList.Row, {
        size: "fluid",
        className: styles.row,
        items: [
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    skin: isDeleted ? 'secondary' : 'standard',
                    children: "".concat(template.name).concat(isDeleted ? ' (Archived)' : '')
                })
            },
            {
                value: /*#__PURE__*/ _jsx(PMMapServiceField, {
                    testId: "".concat(testId, "-mapping"),
                    options: items,
                    value: integratedIds,
                    onChange: async (ids)=>{
                        setIntegratedIds(ids);
                        onMapService(template, ids);
                    }
                }, template.id)
            }
        ]
    }, template.id);
}
function PMMapServicesTable(param) {
    let { pmName, templates: initialTemplates, items, getServiceTemplateIntegratedIds, linkServiceTemplate, refresh } = param;
    const { t: texts } = useLocaleKeys('integrations.practiceManagement');
    const { t: commonTexts } = useLocaleKeys('common');
    const { columnDefs, headerItems } = useMemo(()=>getTableConfig(pmName), [
        pmName
    ]);
    const [search, setSearch] = useState('');
    const [mappedStatusFilter, setMappedStatusFilter] = useState([]);
    const { showErrorToast } = useOverlayContext();
    const [templates, setTemplates] = useState(initialTemplates);
    const filteredTemplates = useMemo(()=>filterTemplates(templates, search, mappedStatusFilter, getServiceTemplateIntegratedIds), [
        templates,
        search,
        mappedStatusFilter,
        getServiceTemplateIntegratedIds
    ]);
    async function mapService(template, itemIds) {
        const result = await handlePromise(linkServiceTemplate(template.id, itemIds));
        if (!result.success) {
            await refresh();
            showErrorToast("Failed to map service ".concat(template.name), 'map-service-error-toast');
        }
        setTemplates(templates.map((t)=>{
            if (t.id === template.id) {
                const currentIntegrations = t.integrations;
                let updatedIntegrations;
                switch(pmName){
                    case PracticeManagement.Keeper:
                        updatedIntegrations = currentIntegrations.setKeeperIntegratedIds(itemIds);
                        break;
                    case PracticeManagement.Karbon:
                        updatedIntegrations = currentIntegrations.setKarbonIntegratedIds(itemIds);
                        break;
                    case PracticeManagement.ClientHub:
                        updatedIntegrations = currentIntegrations.setClientHubIntegratedIds(itemIds);
                        break;
                    case PracticeManagement.FinancialCents:
                        updatedIntegrations = currentIntegrations.setFinancialCentsIntegratedIds(itemIds);
                        break;
                }
                return _object_spread_props(_object_spread({}, t), {
                    integrations: updatedIntegrations || t.integrations
                });
            }
            return t;
        }));
    }
    return /*#__PURE__*/ _jsxs(TableList.Provider, {
        columnDefs: columnDefs,
        children: [
            /*#__PURE__*/ _jsxs(PageContentHeader, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.filtersRow,
                        children: [
                            /*#__PURE__*/ _jsx(Searchbox, {
                                bi: {
                                    name: 'pm_services_search'
                                },
                                testId: "services-search",
                                placeholder: commonTexts.searchPlaceholder(),
                                value: search,
                                onChange: (e)=>setSearch(e.target.value),
                                onClear: ()=>setSearch('')
                            }),
                            /*#__PURE__*/ _jsx(MultiSelectFilterPicker, {
                                bi: {
                                    name: 'pm_mapped_status_filter'
                                },
                                value: mappedStatusFilter,
                                options: mappedStatusFilterOptions,
                                label: texts.mappedStatus(),
                                onChange: setMappedStatusFilter,
                                testId: "mapped-status-filter"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(TableList.Header, {
                        items: headerItems
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                children: filteredTemplates.map((template, idx)=>/*#__PURE__*/ _jsx(PMMapServiceRow, {
                        testId: "service-row-".concat(idx),
                        items: items,
                        template: template,
                        getServiceTemplateIntegratedIds: getServiceTemplateIntegratedIds,
                        onMapService: mapService
                    }, template.id))
            })
        ]
    });
}
export default function PMMapServicesModal(props) {
    const { t: texts } = useLocaleKeys('integrations.practiceManagement');
    const { pmName, dismissModal, getItems } = props;
    const serviceTemplateApi = useServiceTemplateApi();
    const { renderRestfulState } = useRestfulWrapper({
        fetch
    });
    async function fetch() {
        const [templates, items] = await Promise.all([
            serviceTemplateApi.getForMappingToPracticeManagementIntegration(),
            getItems()
        ]);
        return {
            templates,
            items
        };
    }
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({
        testId: "pm-map-services-modal"
    }, modalSize.large), {
        onClose: dismissModal,
        children: /*#__PURE__*/ _jsx(Page, {
            height: "100%",
            header: texts.mapServicesHeader({
                pmName: getPMDetails(pmName).name
            }),
            skin: "secondary",
            children: renderRestfulState({
                renderChild: (param)=>{
                    let { data, refresh } = param;
                    return /*#__PURE__*/ _jsx("div", {
                        "data-testid": "pm-map-services-content",
                        children: data.templates.length > 0 ? /*#__PURE__*/ _jsx(PMMapServicesTable, _object_spread_props(_object_spread({}, props, data), {
                            refresh: refresh
                        })) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            testId: "pm-map-services-no-services",
                            illustration: /*#__PURE__*/ _jsx(NoDataIcon, {}),
                            message: texts.mapServicesEmptyMessage({
                                pmName: getPMDetails(pmName).name
                            }),
                            actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                                text: texts.createAgreement()
                            })
                        })
                    });
                },
                renderLoader: ()=>/*#__PURE__*/ _jsx(LoadingIndicator, {}),
                renderError: (param)=>{
                    let { refresh } = param;
                    return /*#__PURE__*/ _jsx(PageErrorState, {
                        testId: "pm-map-services-fetch-error",
                        message: ErrorText.IntegrationDataRetrieval,
                        onRefresh: refresh,
                        skin: "secondary"
                    });
                }
            })
        })
    }));
}
